import { useEffect, useRef } from "react";
import { useMutation } from "@apollo/client/react";

import { RECORD_SESSION_ACTIVITY } from "@/graphql/mutations/sessions";

// How often we tick the local counter.
const TICK_MS = 1000;
// How often we flush accumulated seconds to the server.
const FLUSH_MS = 15_000;
// Stop counting after this much inactivity (no pointer/keyboard/touch input).
const IDLE_MS = 90_000;

/**
 * Tracks the time a student is *actively* working on a session and periodically
 * reports it to the server (accumulated into `session.activeSeconds`).
 *
 * "Active" means: the hook is enabled, the tab is visible, and the student has
 * interacted within the last IDLE_MS. Time is accumulated in whole seconds and
 * flushed in small chunks, so pausing / closing the tab / resuming later all
 * add up to real working time rather than wall-clock elapsed time.
 *
 * If an `activeProblemId` is supplied, the same active seconds are *also*
 * attributed to that problem (accumulated into `problem.activeSeconds`), so we
 * can later tell which task the student spent the longest on. Whenever the
 * active problem changes we flush first, so each chunk is credited to exactly
 * one problem.
 *
 * @param sessionId       the session being worked on (null disables tracking)
 * @param enabled         whether solving is in progress (e.g. status === "GENERATED")
 * @param activeProblemId the problem currently highlighted as active, if any
 */
export function useSessionTimer(
  sessionId: string | null | undefined,
  enabled: boolean,
  activeProblemId?: string | null
) {
  const [recordActivity] = useMutation(RECORD_SESSION_ACTIVITY);

  // Seconds counted locally but not yet sent to the server (session total).
  const pendingRef = useRef(0);
  // Of those pending seconds, how many belong to the currently active problem.
  const problemPendingRef = useRef(0);
  // The problem those pending problem-seconds are credited to.
  const activeProblemRef = useRef<string | null>(activeProblemId ?? null);
  // Timestamp (ms) of the student's last interaction.
  const lastActivityRef = useRef(Date.now());
  // Keep the latest mutation fn in a ref so listeners don't capture a stale one.
  const recordRef = useRef(recordActivity);
  recordRef.current = recordActivity;
  // Expose the current flush fn so the active-problem effect can flush on change.
  const flushRef = useRef<() => void>(() => {});

  useEffect(() => {
    if (!sessionId || !enabled) return;

    const flush = () => {
      const seconds = pendingRef.current;
      const problemSeconds = problemPendingRef.current;
      const exerciseId = activeProblemRef.current;
      if (seconds <= 0 && problemSeconds <= 0) return;
      pendingRef.current = 0;
      problemPendingRef.current = 0;
      recordRef.current({
        variables: {
          sessionId,
          seconds,
          // `exerciseId`, not `problemId` — the same rename that broke
          // `persistAnswer` in SessionDetailPage. It bit quieter here because
          // this variable is nullable: the mutation succeeded, the session's own
          // seconds were stored, and only the per-question attribution was
          // silently dropped. Hence 413 session seconds over ten questions all
          // reading 0.
          exerciseId: problemSeconds > 0 ? exerciseId : null,
          problemSeconds,
        },
      }).catch(() => {
        // On failure, put the seconds back so they're retried on the next flush.
        pendingRef.current += seconds;
        problemPendingRef.current += problemSeconds;
      });
    };
    flushRef.current = flush;

    const markActive = () => {
      lastActivityRef.current = Date.now();
    };

    const activityEvents: (keyof DocumentEventMap)[] = [
      "pointerdown",
      "keydown",
      "mousemove",
      "touchstart",
      "wheel",
    ];
    activityEvents.forEach((evt) =>
      document.addEventListener(evt, markActive, { passive: true })
    );

    // Reset the activity clock when the effect starts so we don't immediately
    // count against a stale idle timestamp.
    lastActivityRef.current = Date.now();

    const tick = window.setInterval(() => {
      if (document.visibilityState !== "visible") return;
      if (Date.now() - lastActivityRef.current > IDLE_MS) return;
      pendingRef.current += TICK_MS / 1000;
      if (activeProblemRef.current) problemPendingRef.current += TICK_MS / 1000;
    }, TICK_MS);

    const flushInterval = window.setInterval(flush, FLUSH_MS);

    const handleVisibility = () => {
      // Flush whatever we have whenever the tab is backgrounded.
      if (document.visibilityState === "hidden") flush();
    };
    document.addEventListener("visibilitychange", handleVisibility);
    // pagehide covers real navigations away / tab close on most browsers.
    window.addEventListener("pagehide", flush);

    return () => {
      activityEvents.forEach((evt) => document.removeEventListener(evt, markActive));
      document.removeEventListener("visibilitychange", handleVisibility);
      window.removeEventListener("pagehide", flush);
      window.clearInterval(tick);
      window.clearInterval(flushInterval);
      // Final flush when the component unmounts (SPA navigation, session complete).
      flush();
      flushRef.current = () => {};
    };
  }, [sessionId, enabled]);

  // When the active problem changes, flush accrued time to the *previous*
  // problem before crediting subsequent ticks to the new one.
  useEffect(() => {
    flushRef.current();
    activeProblemRef.current = activeProblemId ?? null;
  }, [activeProblemId]);
}
